import React, { useState, useEffect } from "react";
import { Link, BarChart3, Copy, Check } from "lucide-react";
import "./ShareSurveyModal.css";
import { getBackendUrl } from "../config/env";

interface ShareSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  surveyId: string;
  surveyTitle: string;
}

interface SurveyStats {
  totalVisits: number;
  completedResponses: number;
  averageCompletionTime: number | null;
}

const ShareSurveyModal: React.FC<ShareSurveyModalProps> = ({
  isOpen,
  onClose,
  surveyId,
  surveyTitle,
}) => {
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<SurveyStats>({
    totalVisits: 0,
    completedResponses: 0,
    averageCompletionTime: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && surveyId) {
      fetchSurveyStats();
    }
  }, [isOpen, surveyId]);

  const fetchSurveyStats = async () => {
    setLoading(true);
    try {
      // 통계 데이터 가져오기
      const response = await fetch(
        `${getBackendUrl()}/api/surveys/${surveyId}/stats`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalVisits: data.totalVisits || 0,
          completedResponses: data.completedResponses || 0,
          averageCompletionTime: data.averageCompletionTime || null,
        });
      } else {
        // 응답 목록에서 통계 계산 (fallback)
        await fetchStatsFromResponses();
      }
    } catch (error) {
      console.error("Failed to fetch survey stats:", error);
      // fallback으로 응답 목록에서 통계 계산
      await fetchStatsFromResponses();
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsFromResponses = async () => {
    try {
      const response = await fetch(
        `${getBackendUrl()}/api/responses?surveyId=${surveyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const responses = await response.json();

        // 완료된 응답 수 계산
        const completedResponses = responses.filter(
          (r: any) => r.status === "completed"
        ).length;

        // 평균 완료 시간 계산
        const completionTimes = responses
          .filter((r: any) => r.status === "completed" && r.completionTime)
          .map((r: any) => r.completionTime);

        const averageCompletionTime =
          completionTimes.length > 0
            ? completionTimes.reduce((a: number, b: number) => a + b, 0) /
              completionTimes.length
            : null;

        setStats({
          totalVisits: responses.length, // 방문 수는 별도 API가 없으면 응답 수로 대체
          completedResponses,
          averageCompletionTime,
        });
      }
    } catch (error) {
      console.error("Failed to fetch responses:", error);
    }
  };

  if (!isOpen) return null;

  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/survey/${surveyId}`;

  const copyToClipboard = (text: string) => {
    // 클립보드 API가 사용 가능한지 확인
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          fallbackCopyTextToClipboard(text);
        });
    } else {
      // fallback 방법 사용
      fallbackCopyTextToClipboard(text);
    }
  };

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // 화면에 보이지 않게 설정
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert("복사에 실패했습니다. 수동으로 복사해주세요.");
      }
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
      alert("복사에 실패했습니다. 수동으로 복사해주세요.");
    }

    document.body.removeChild(textArea);
  };

  const formatTime = (minutes: number | null) => {
    if (minutes === null) return "-";
    if (minutes < 1) return "1분 미만";
    if (minutes < 60) return `${Math.round(minutes)}분`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}시간 ${mins}분`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>설문조사 공유하기</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="share-section">
            <h3>
              <Link
                size={20}
                style={{
                  marginRight: "8px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
              설문조사 링크
            </h3>
            <div className="url-container">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="url-input"
              />
              <button
                className={`copy-btn ${copied ? "copied" : ""}`}
                onClick={() => copyToClipboard(shareUrl)}
              >
                {copied ? (
                  <>
                    <Check
                      size={16}
                      style={{
                        marginRight: "4px",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    복사됨
                  </>
                ) : (
                  <>
                    <Copy
                      size={16}
                      style={{
                        marginRight: "4px",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                    복사
                  </>
                )}
              </button>
            </div>
            <p className="helper-text">
              이 링크를 공유하면 누구나 설문에 참여할 수 있습니다
            </p>
          </div>

          <div className="share-stats">
            <h3>
              <BarChart3
                size={20}
                style={{
                  marginRight: "8px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
              공유 통계
            </h3>
            {loading ? (
              <div className="stats-loading">
                <p>통계 데이터를 불러오는 중...</p>
              </div>
            ) : (
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">총 방문</span>
                  <span className="stat-value">
                    {stats.totalVisits.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">완료된 응답</span>
                  <span className="stat-value">
                    {stats.completedResponses.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">평균 완료 시간</span>
                  <span className="stat-value">
                    {formatTime(stats.averageCompletionTime)}
                  </span>
                </div>
              </div>
            )}
            {stats.totalVisits > 0 && (
              <div className="completion-rate">
                <p className="rate-label">응답 완료율</p>
                <div className="rate-bar">
                  <div
                    className="rate-fill"
                    style={{
                      width: `${(
                        (stats.completedResponses / stats.totalVisits) *
                        100
                      ).toFixed(1)}%`,
                    }}
                  />
                </div>
                <p className="rate-text">
                  {(
                    (stats.completedResponses / stats.totalVisits) *
                    100
                  ).toFixed(1)}
                  %
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSurveyModal;
